/**
 * LabRangeBar — Visual lab range indicator component
 *
 * Displays a patient's lab value on a reference range spectrum with
 * color-coded status badge and optional delta indicator.
 *
 * Supports two palettes:
 *   - provider: Nimbus Medical (brand-teal/amber/copper/red) — for clinic portals
 *   - patient:  Soft Clinical (teal-600/yellow-600/orange-500/rose-600) — for patient portal
 *
 * Part of IntelliHealth UX Upgrades Phase 1.
 *
 * @module IntelliHealth/LabRangeBar
 * @author Dr. Jobby John <jobby@nimbushealthcare.com>
 * @copyright Nimbus Healthcare Corporation. Patent pending.
 */

import { cn } from '@/lib/utils'
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  AlertOctagon,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

export type LabStatus =
  | 'normal'
  | 'borderline-low'
  | 'borderline-high'
  | 'low'
  | 'high'
  | 'critical-low'
  | 'critical-high'

export type LabPalette = 'provider' | 'patient'

export interface LabRange {
  low: number
  high: number
  criticalLow?: number
  criticalHigh?: number
}

export interface LabRangeBarProps {
  /** Display name of the biomarker */
  label: string
  /** Patient's actual value */
  value: number
  /** Unit of measurement */
  unit: string
  /** Reference range */
  range: LabRange
  /** Previous value for delta indicator */
  previousValue?: number
  /** Compact mode for inline use in recommendation text */
  compact?: boolean
  /** Color palette: 'provider' (Nimbus Medical) or 'patient' (Soft Clinical) */
  palette?: LabPalette
  /** AI-generated commentary for abnormal values (IntelliHealth integration) */
  aiCommentary?: string | null
  /** Additional CSS classes */
  className?: string
}

// ─── Classification ───────────────────────────────────────────────────────────

export function classifyLabValue(value: number, range: LabRange): LabStatus {
  const { low, high, criticalLow, criticalHigh } = range
  const span = high - low
  const borderlineThreshold = span * 0.1

  if (criticalLow !== undefined && value <= criticalLow) return 'critical-low'
  if (criticalHigh !== undefined && value >= criticalHigh) return 'critical-high'
  if (value < low - span) return 'critical-low'
  if (value > high + span) return 'critical-high'
  if (value < low) return 'low'
  if (value > high) return 'high'
  if (value < low + borderlineThreshold) return 'borderline-low'
  if (value > high - borderlineThreshold) return 'borderline-high'
  return 'normal'
}

// ─── Palette Configs ──────────────────────────────────────────────────────────

interface StatusStyle {
  label: string
  icon: typeof CheckCircle2
  badgeClass: string
  barClass: string
  zoneClass: string
  zoneBorderClass: string
}

type StatusGroup = 'normal' | 'borderline' | 'abnormal' | 'critical'

function statusToGroup(status: LabStatus): StatusGroup {
  if (status === 'normal') return 'normal'
  if (status.includes('borderline')) return 'borderline'
  if (status.includes('critical')) return 'critical'
  return 'abnormal'
}

function statusLabel(status: LabStatus): string {
  if (status === 'normal') return 'NORMAL'
  if (status.includes('borderline')) return 'BORDERLINE'
  if (status.includes('critical')) return 'CRITICAL'
  return status === 'low' ? 'LOW' : 'HIGH'
}

const PALETTES: Record<LabPalette, Record<StatusGroup, { badgeClass: string; barClass: string }>> = {
  // B: Nimbus Medical — brand-aligned, medically intuitive
  provider: {
    normal:     { badgeClass: 'bg-brand-teal text-white',                          barClass: 'bg-brand-teal' },
    borderline: { badgeClass: 'bg-amber-500 text-white',                           barClass: 'bg-amber-500' },
    abnormal:   { badgeClass: 'bg-brand-copper text-white',                        barClass: 'bg-brand-copper' },
    critical:   { badgeClass: 'bg-red-600 text-white animate-pulse',               barClass: 'bg-red-600' },
  },
  // C: Soft Clinical — muted, calming, patient-friendly
  patient: {
    normal:     { badgeClass: 'bg-teal-600 text-white',                            barClass: 'bg-teal-600' },
    borderline: { badgeClass: 'bg-yellow-600 text-white',                          barClass: 'bg-yellow-600' },
    abnormal:   { badgeClass: 'bg-orange-500 text-white',                          barClass: 'bg-orange-500' },
    critical:   { badgeClass: 'bg-rose-600 text-white animate-pulse',              barClass: 'bg-rose-600' },
  },
}

const ZONE_STYLES: Record<LabPalette, { zoneClass: string; zoneBorderClass: string }> = {
  provider: { zoneClass: 'bg-brand-teal/10',  zoneBorderClass: 'border-brand-teal/25' },
  patient:  { zoneClass: 'bg-teal-600/8',     zoneBorderClass: 'border-teal-600/20' },
}

const STATUS_ICONS: Record<StatusGroup, typeof CheckCircle2> = {
  normal: CheckCircle2,
  borderline: AlertTriangle,
  abnormal: XCircle,
  critical: AlertOctagon,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function calcPosition(value: number, range: LabRange): number {
  const { low, high } = range
  const span = high - low
  const barMin = low - span * 0.5
  const barMax = high + span * 0.5
  const barSpan = barMax - barMin
  const pos = ((value - barMin) / barSpan) * 100
  return Math.max(2, Math.min(98, pos))
}

function calcNormalZone(range: LabRange): { left: number; width: number } {
  const { low, high } = range
  const span = high - low
  const barMin = low - span * 0.5
  const barMax = high + span * 0.5
  const barSpan = barMax - barMin
  const left = ((low - barMin) / barSpan) * 100
  const right = ((high - barMin) / barSpan) * 100
  return { left, width: right - left }
}

function formatDelta(current: number, previous: number): {
  text: string
  icon: typeof TrendingUp
} {
  const delta = current - previous
  const sign = delta > 0 ? '+' : ''
  return {
    text: `${sign}${delta.toFixed(1)}`,
    icon: delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus,
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LabRangeBar({
  label,
  value,
  unit,
  range,
  previousValue,
  compact = false,
  palette = 'provider',
  aiCommentary,
  className,
}: LabRangeBarProps) {
  const status = classifyLabValue(value, range)
  const group = statusToGroup(status)
  const colors = PALETTES[palette][group]
  const zones = ZONE_STYLES[palette]
  const StatusIcon = STATUS_ICONS[group]
  const badgeLabel = statusLabel(status)
  const position = calcPosition(value, range)
  const normalZone = calcNormalZone(range)

  if (compact) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 font-inter text-sm', className)}>
        <span className="font-mono font-medium">{value} {unit}</span>
        <span className={cn(
          'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-montserrat font-semibold leading-none',
          colors.badgeClass
        )}>
          <StatusIcon className="h-2.5 w-2.5" />
          {badgeLabel}
        </span>
      </span>
    )
  }

  return (
    <div className={cn('w-full space-y-1.5', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="font-montserrat font-semibold text-sm text-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-foreground">{value} {unit}</span>
          <span className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-montserrat font-semibold',
            colors.badgeClass
          )}>
            <StatusIcon className="h-3 w-3" />
            {badgeLabel}
          </span>
        </div>
      </div>

      {/* Range Bar */}
      <div className="relative h-3 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn('absolute inset-y-0 border-x', zones.zoneClass, zones.zoneBorderClass)}
          style={{ left: `${normalZone.left}%`, width: `${normalZone.width}%` }}
        />
        <div
          className={cn('absolute top-0 h-full w-1.5 rounded-full shadow-sm transition-all', colors.barClass)}
          style={{ left: `calc(${position}% - 3px)` }}
        />
      </div>

      {/* Range labels */}
      <div className="flex justify-between text-[10px] font-inter text-muted-foreground">
        <span>Low</span>
        <span>Normal ({range.low}–{range.high} {unit})</span>
        <span>High</span>
      </div>

      {/* Delta */}
      {previousValue !== undefined && previousValue !== value && (
        <div className="flex items-center gap-1 text-xs font-inter text-muted-foreground">
          {(() => {
            const delta = formatDelta(value, previousValue)
            const DeltaIcon = delta.icon
            return (
              <>
                <DeltaIcon className="h-3 w-3" />
                <span className="font-mono">{delta.text} {unit}</span>
                <span>from previous</span>
              </>
            )
          })()}
        </div>
      )}

      {/* AI Commentary — IntelliHealth interpretation for abnormal values */}
      {aiCommentary && group !== 'normal' && (
        <div className="flex items-start gap-1.5 rounded-md bg-blue-50 border border-blue-100 px-2.5 py-1.5 text-xs text-blue-800">
          <Sparkles className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
          <span>{aiCommentary}</span>
        </div>
      )}
    </div>
  )
}

export default LabRangeBar
